import type { Discount, Market } from './schemas';

/**
 * DatoCMS market records used by api-v4 `getProductAndPlanData`.
 * Keep in sync with `@cove/dato` DEFAULT_MARKET and the Canada market item.
 */
export const DATO_MARKETS: Record<'US' | 'CA', { id: string; isoCode: 'US' | 'CA' }> =
  {
    US: { id: 'EbJM-8poSBO4eWrYJ4Ng8A', isoCode: 'US' },
    CA: { id: 'fke7pm5gRQmxNfm3e_KsKg', isoCode: 'CA' }
  };

export function getDatoMarket(
  country: 'US' | 'CA' = 'US'
): { id: string; isoCode: 'US' | 'CA' } {
  return DATO_MARKETS[country] ?? DATO_MARKETS.US;
}

/**
 * DatoCMS `OR` filter matching api-v4 `getMarketORFilter`.
 * US includes records with no markets set; other countries require the market id.
 */
export function getMarketORFilter(market: { id: string; isoCode: string }): string {
  return `${
    market.isoCode === 'US' ? '{markets: {exists: false}}, ' : ''
  }{markets: {anyIn: "${market.id}"}}`;
}

export const DATO_DISCOUNT_FIELDS = `
  id
  active
  activateCondition
  appliesTo
  conditionProductsQuantifier
  conditionProducts {
    sku
  }
  description
  minimumOrderTotal
  minimumOrderTotalAfterDiscount
  freeMonths
  initialPaymentRequired
  exclusiveDiscount
  maxQuantity
  discountType
  discountAmount
  removeFromBase
  removeFromBaseQty
  removeFromSalePrice
  priority
  restrictTo {
    id
    sku
  }
  additionalDiscounts {
    id
  }
  applyAfterDiscounts {
    id
  }
  overrideDiscounts {
    id
  }
  markets {
    id
    isoCode
  }
`;

export type DatoDiscountRecord = Omit<
  Discount,
  'restrictTo' | 'conditionProducts'
> & {
  restrictTo?: Array<{ id?: string; sku: string } | string>;
  conditionProducts?: Array<{ id?: string; sku: string } | string>;
  markets?: Market[];
};

export type PartnerSalesConfigDiscounts = {
  discountFilter?: string | null;
  discounts?: DatoDiscountRecord[];
  discountsList?: DatoDiscountRecord[];
};

function toSkuList(
  list?: Array<{ sku: string } | string>
): string[] {
  return (list ?? []).map((item) =>
    typeof item === 'string' ? item : item.sku
  );
}

function isDiscountInMarket(
  discount: DatoDiscountRecord,
  market: { id: string; isoCode: string }
): boolean {
  if (!discount.active) {
    return false;
  }

  const discountMarkets = discount.markets ?? [];
  if (discountMarkets.length === 0) {
    return market.isoCode === 'US';
  }

  return discountMarkets.some((item) => item.id === market.id);
}

function mapDiscountSkus(discount: DatoDiscountRecord): Discount {
  return {
    ...discount,
    restrictTo: toSkuList(discount.restrictTo),
    conditionProducts: toSkuList(discount.conditionProducts)
  };
}

/**
 * Apply partner sales-config discountFilter the same way api-v4
 * `getProductAndPlanData` does, then map product refs to SKUs.
 */
export function resolvePartnerDiscounts({
  allDiscounts,
  partnerSalesConfig,
  market
}: {
  allDiscounts: DatoDiscountRecord[];
  partnerSalesConfig?: PartnerSalesConfigDiscounts | null;
  market: { id: string; isoCode: string };
}): Discount[] {
  let discounts = [...(allDiscounts ?? [])];
  const partnerDiscounts =
    partnerSalesConfig?.discounts ?? partnerSalesConfig?.discountsList ?? [];

  if (partnerSalesConfig) {
    if (partnerSalesConfig.discountFilter === 'whitelist') {
      discounts = partnerDiscounts.filter((discount) =>
        isDiscountInMarket(discount, market)
      );
    } else if (partnerSalesConfig.discountFilter === 'blacklist') {
      const blacklist = partnerDiscounts.map(({ id }) => id);
      discounts = discounts.filter(({ id }) => !blacklist.includes(id));
    } else if (partnerSalesConfig.discountFilter === 'none') {
      discounts = [];
    }
  }

  return discounts.map(mapDiscountSkus);
}
